/**
 * Adapters mapping account GraphQL responses to flat view-model types for
 * the Manage Account page.
 */

import type { ChipResellerContactInfoInput, ResellerTaxExemptType } from '@/types/gql-op-types';
import type { RescueAccountInfoData } from './accountQueries';

export interface AccountOrganization {
  name: string;
  /** The logged-in rescue user's email — org identity, not editable here. */
  email: string;
  website: string;
  taxExemptType: ResellerTaxExemptType | null;
}

export interface AccountPrimaryContact {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export interface AccountAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export interface RescueAccountView {
  /** Reseller-backed sections (contact, addresses, org type) render only when true. */
  isReseller: boolean;
  resellerId: string | null;
  /** The rescue household's id — org-name saves rename this alongside the reseller. */
  householdId: string | null;
  organization: AccountOrganization;
  primaryContact: AccountPrimaryContact | null;
  address: AccountAddress | null;
  shippingAddress: AccountAddress | null;
}

/**
 * Flattened view of the reseller's contactInfo covering every field the
 * current `ChipResellerContactInfoInput` accepts. Edit modals overlay their
 * changes on this before building mutation variables, so saving one section
 * never drops another section's values.
 */
export interface AccountContactInfoFlat {
  phone: string;
  website: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export function adaptAccountInfo(data: RescueAccountInfoData | undefined): RescueAccountView | null {
  const user = data?.currentUser;
  if (!user) {
    return null;
  }

  const reseller = user.chipReseller ?? null;
  const contact = reseller?.contactInfo ?? null;
  const shipping = reseller?.shippingAddress ?? null;
  const rescueHousehold = user.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');

  return {
    isReseller: !!reseller,
    resellerId: reseller?.id ?? null,
    householdId: rescueHousehold?.household.id ?? null,
    organization: {
      name: reseller?.name ?? rescueHousehold?.household.name ?? '',
      email: user.email,
      website: contact?.website ?? '',
      taxExemptType: reseller?.taxExemptType ?? null,
    },
    primaryContact: reseller
      ? {
          firstName: contact?.firstName ?? '',
          lastName: contact?.lastName ?? '',
          phone: contact?.primaryPhone ?? '',
          email: contact?.primaryEmail ?? '',
        }
      : null,
    address: reseller
      ? {
          line1: contact?.line1 ?? '',
          line2: contact?.line2 ?? '',
          city: contact?.city ?? '',
          state: contact?.state ?? '',
          zip: contact?.zipcode ?? '',
        }
      : null,
    shippingAddress: reseller
      ? {
          line1: shipping?.line1 ?? '',
          line2: shipping?.line2 ?? '',
          city: shipping?.city ?? '',
          state: shipping?.state ?? '',
          zip: shipping?.zip ?? '',
        }
      : null,
  };
}

/** Current contactInfo values in the flat shape edit modals overlay onto. */
export function contactInfoFromView(view: RescueAccountView): AccountContactInfoFlat {
  return {
    phone: view.primaryContact?.phone ?? '',
    website: view.organization.website,
    line1: view.address?.line1 ?? '',
    line2: view.address?.line2 ?? '',
    city: view.address?.city ?? '',
    state: view.address?.state ?? '',
    zip: view.address?.zip ?? '',
  };
}

/**
 * Build the `contactInfo` mutation input from flat values. Mirrors the legacy
 * /vet/account behavior of omitting empty fields rather than sending ''.
 */
export function toContactInfoInput(flat: AccountContactInfoFlat): ChipResellerContactInfoInput {
  const input: ChipResellerContactInfoInput = {};
  if (flat.phone) {
    input.primaryPhone = flat.phone;
  }
  if (flat.website) {
    input.website = flat.website;
  }
  if (flat.line1) {
    input.line1 = flat.line1;
  }
  if (flat.line2) {
    input.line2 = flat.line2;
  }
  if (flat.city) {
    input.city = flat.city;
  }
  if (flat.state) {
    input.state = flat.state;
  }
  if (flat.zip) {
    input.zipcode = flat.zip;
  }
  return input;
}

/** "Pam Whitcomb" from first/last, or '' when neither is set. */
export function formatContactName(contact: AccountPrimaryContact | null): string {
  if (!contact) {
    return '';
  }
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
}

/**
 * Format a US phone for read-only display, e.g. "(978) 555-0142". Accepts a
 * 10-digit number (or 1 + 10 digits); returns the input unchanged if it isn't
 * one of those (so partial/foreign numbers and empty strings pass through).
 */
export function formatUsPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) {
    return phone;
  }
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}
